import {
  Badge,
  Button,
  Card,
  Center,
  Combobox,
  Group,
  Loader,
  Pagination,
  Stack,
  Table,
  Text,
  useCombobox,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure, useHotkeys, useSessionStorage } from '@mantine/hooks';
import { IconChevronDown, IconFilter, IconFilterOff, IconProgressCheck } from '@tabler/icons-react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { PageTitle } from 'components/PageTitle';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { countActiveFilters } from 'modules/Kanban/utils/filters';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { isNonNullish } from 'remeda';
import { handleRowAuxClick, handleRowClick } from 'shared/utils/table';

import { type LoadAiCallsVariables, useLoadAiCalls } from '../api';
import { FilterDrawer } from '../components';
import { useBuildAiCallColumns } from '../hooks/use-build-ai-call-columns';

const STORAGE_KEY = 'ai-calls-filters';

export type AiCallsFilter = Omit<LoadAiCallsVariables, 'page' | 'pageLimit'>;

export const AiCallsIndex = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const profile = useProfile();

  const columns = useBuildAiCallColumns();
  const [currentPage, setCurrentPage] = useState(1);

  const [isDrawerOpen, { open: openDrawer, close: closeDrawer, toggle: toggleDrawerOpen }] =
    useDisclosure(false);
  useHotkeys([['f', toggleDrawerOpen]]);

  const filterForm = useForm<AiCallsFilter>({
    onValuesChange: values => window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values)),
  });

  useEffect(() => {
    const storedValue = window.sessionStorage.getItem(STORAGE_KEY);
    if (isNonNullish(storedValue)) filterForm.setValues(JSON.parse(storedValue));
  }, []);

  const [pageLimit, setPageLimit] = useSessionStorage({
    key: 'aiCallsIndexPageLimit',
    defaultValue: profile.preferences.itemsPerPage,
  });

  const { aiCalls, isAiCallsFetching } = useLoadAiCalls({
    page: currentPage,
    pageLimit,
    ...filterForm.values,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const table = useReactTable({
    data: aiCalls.data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const hasNoAiCalls = aiCalls?.data.length === 0 && !isAiCallsFetching;

  const options = [10, 20, 50, 100].map(item => (
    <Combobox.Option value={item.toString()} key={item}>
      {item}
    </Combobox.Option>
  ));

  const activeFiltersCount = countActiveFilters(filterForm.values);

  return (
    <>
      <Stack h="100%" gap={0}>
        <PageTitle>
          {t(i18nKeys.AI_CALL)}
          <PageTitle.Actions>
            <Button
              variant="default"
              leftSection={<IconFilter size={18} stroke={1.5} />}
              rightSection={
                <Badge color={activeFiltersCount > 0 ? 'purple' : 'gray'}>
                  {activeFiltersCount}
                </Badge>
              }
              onClick={openDrawer}
            >
              {t(i18nKeys.AI_CALLS.FILTERS)}
            </Button>
            {activeFiltersCount > 0 && (
              <Button variant="default" onClick={filterForm.reset}>
                <IconFilterOff stroke={1.5} size={18} />
              </Button>
            )}
          </PageTitle.Actions>
        </PageTitle>
        <Card radius={8} p={0}>
          <Group style={{ overflowY: 'auto' }}>
            <Table
              highlightOnHover
              highlightOnHoverColor="purple.0"
              horizontalSpacing="md"
              verticalSpacing="space3"
              stickyHeader
              fz={12}
            >
              <Table.Thead bg="purple.1" style={{ fontSize: 14 }}>
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Th key={header.id} c="purple.5" fw={400}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Thead>
              <Table.Tbody>
                {isAiCallsFetching && (
                  <Table.Tr>
                    <Table.Td colSpan={columns.length}>
                      <Center py="xl">
                        <Loader />
                      </Center>
                    </Table.Td>
                  </Table.Tr>
                )}
                {table.getRowModel().rows.map(row => (
                  <Table.Tr
                    key={row.id}
                    onClick={event =>
                      handleRowClick(event, `/reminders/ai-calls/${row.original.id}`, history.push)
                    }
                    onAuxClick={event =>
                      handleRowAuxClick(event, `/reminders/ai-calls/${row.original.id}`)
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Table.Td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Group>
          {hasNoAiCalls && (
            <Center mb="md" w="100%">
              <Stack align="center">
                <IconProgressCheck size={75} color="gray" />
                <Text size="sm">{t(i18nKeys.AI_CALLS.EMPTY_STATE)}</Text>
              </Stack>
            </Center>
          )}
          <Group
            justify="space-between"
            p="sm"
            style={theme => ({
              borderTop: `1px solid ${theme.colors.gray[2]}`,
            })}
          >
            <Combobox
              store={combobox}
              position="bottom-start"
              withArrow
              onOptionSubmit={val => {
                setPageLimit(Number(val));
                combobox.closeDropdown();
              }}
            >
              <Combobox.Target>
                <Group
                  onClick={() => combobox.toggleDropdown()}
                  style={{ cursor: 'pointer' }}
                  gap={4}
                >
                  <Text size="sm">
                    {pageLimit} {t(i18nKeys.PER_PAGE)}
                  </Text>
                  <IconChevronDown stroke={1.5} size={16} />
                </Group>
              </Combobox.Target>
              <Combobox.Dropdown>
                <Combobox.Options>{options}</Combobox.Options>
              </Combobox.Dropdown>
            </Combobox>
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={aiCalls?.pagination.totalPages ?? 1}
              size="xs"
            />
          </Group>
        </Card>
      </Stack>
      <FilterDrawer isOpen={isDrawerOpen} close={closeDrawer} form={filterForm} />
    </>
  );
};
